import { useIntl } from '@faceup/localization'
import { CountryFlag } from '@faceup/ui'
import { Space } from '@faceup/ui-base'
import {
  Country,
  convertCountryToCountryName,
  isCountrySanctioned,
  PHONE_PREFIXES,
} from '@faceup/utils'
import type { FieldValues } from 'react-hook-form'
import { Select, type SelectProps } from './Select'

type CountrySelectProps<T extends FieldValues> = {
  countries?: Country[]
  withPhoneCode?: boolean
  showSearch?: never
  'data-test'?: string
  includeSanctionedCountries?: boolean
} & Omit<SelectProps<T, Country>, 'searchable' | 'data'>

export const CountrySelect = <T extends FieldValues>({
  countries,
  withPhoneCode,
  includeSanctionedCountries = false,
  ...props
}: CountrySelectProps<T>) => {
  const { locale } = useIntl()

  const usedCountries = includeSanctionedCountries
    ? Object.values(Country)
    : Object.values(Country).filter(country => !isCountrySanctioned(country))

  return (
    <Select
      {...props}
      showSearch
      data-test={`${props['data-test']}-input`}
      filterOption={(input, option) =>
        (option?.label?.toString() ?? '').toLowerCase().includes(input.toLowerCase()) ||
        (withPhoneCode ? (PHONE_PREFIXES[option.value] ?? '').includes(input) : false)
      }
      popupRender={menu => <div data-test={`${props['data-test']}-menu`}>{menu}</div>}
      options={usedCountries.map(country => ({
        value: country,
        label: convertCountryToCountryName(country, locale),
      }))}
      optionRender={option => (
        <Space>
          <CountryFlag country={option.value} />
          {option.label}
          {withPhoneCode ? ` (${PHONE_PREFIXES[option.value]})` : ''}
        </Space>
      )}
    />
  )
}
